import { useCallback, useEffect, useState } from 'react';

const MIN_SIDEBAR_WIDTH = 320;
const DEFAULT_SIDEBAR_WIDTH = 380;
const MAX_SIDEBAR_WIDTH = 640;

function clampSidebarWidth(width) {
  return Math.min(Math.max(width, MIN_SIDEBAR_WIDTH), MAX_SIDEBAR_WIDTH);
}

function calculateSidebarWidth(containerRef, clientX) {
  const containerRect = containerRef.current?.getBoundingClientRect();
  if (!containerRect) {
    return null;
  }

  return clampSidebarWidth(clientX - containerRect.left);
}

function useSidebarResizeListeners(isResizing, handleMouseMove, handleMouseUp) {
  useEffect(() => {
    if (!isResizing) {
      return undefined;
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [handleMouseMove, handleMouseUp, isResizing]);
}

export function useResizableSidebar(containerRef, initialWidth = DEFAULT_SIDEBAR_WIDTH) {
  const [sidebarWidth, setSidebarWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const getSidebarWidth = useCallback(
    (clientX) => calculateSidebarWidth(containerRef, clientX),
    [containerRef]
  );

  const updateSidebarWidth = useCallback((clientX) => {
    const resolvedWidth = getSidebarWidth(clientX);
    if (resolvedWidth == null) {
      return;
    }

    setSidebarWidth(resolvedWidth);
  }, [getSidebarWidth]);

  const handleMouseMove = useCallback((event) => {
    if (!isResizing) {
      return;
    }

    updateSidebarWidth(event.clientX);
  }, [isResizing, updateSidebarWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useSidebarResizeListeners(isResizing, handleMouseMove, handleMouseUp);

  return {
    sidebarWidth,
    isResizing,
    handleMouseDown,
  };
}
